import { Link } from '@tanstack/react-router';
import { Check, CircleAlert, Lock, LoaderCircle, Wrench } from 'lucide-react';
import type { AssistantLink, MessagePart, ProposalCard as Proposal } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { isSafeAppPath, pathTo } from '@/lib/search';
import { Callout } from '@/ui';
import s from './Assistant.module.css';
import { ProposalCard } from './ProposalCard';
import { RichText } from './RichText';
import type { LiveTurn, ToolChip } from './useConversation';

export function toolLabel(name: string): string {
  const last = name.split('.').pop() ?? name;
  const words = last.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function ToolChipView({ tool }: { tool: ToolChip }) {
  const running = tool.status === 'running';
  const Icon = running ? LoaderCircle : tool.status === 'denied' ? Lock : tool.status === 'error' ? CircleAlert : Check;
  return (
    <span
      className={cx(s.tool, running && s.toolRunning, (tool.status === 'error' || tool.status === 'denied') && s.toolError)}
      title={tool.summary ?? undefined}
    >
      <Wrench className={s.toolIcon} aria-hidden />
      <span>{toolLabel(tool.name)}</span>
      {tool.summary && !running && <span className={s.toolSummary}>· {tool.summary}</span>}
      <Icon className={cx(s.toolIcon, running && s.spin)} aria-label={running ? t('Running') : t('Done')} />
    </span>
  );
}

function Links({ links }: { links: AssistantLink[] }) {
  const safe = links.filter((l) => isSafeAppPath(l.href));
  if (!safe.length) return null;
  return (
    <div className={s.links}>
      {safe.map((l, i) => (
        <Link key={`${l.href}-${i}`} {...pathTo(l.href)} className={s.linkChip}>
          {l.label}
        </Link>
      ))}
    </div>
  );
}

export function UserBubble({ text }: { text: string }) {
  return <div className={s.user}>{text}</div>;
}

function Thinking() {
  return (
    <div className={s.thinking}>
      <span />
      <span />
      <span />
      <span className="sr-only">{t('Thinking…')}</span>
    </div>
  );
}

/** A stored assistant message: text plus its tool, link and proposal parts. `working` = the
 * server is still writing it (the thread is re-read until it is done). */
export function AssistantMessage({ text, parts, status }: { text: string; parts: MessagePart[]; status: string }) {
  const working = status === 'working';
  const tools: ToolChip[] = [];
  const links: AssistantLink[] = [];
  const proposals: Proposal[] = [];
  parts.forEach((p, i) => {
    if (p.kind === 'tool') {
      tools.push({ id: String(i), name: p.name, status: p.status, summary: p.summary });
      if (p.links) links.push(...p.links);
    } else if (p.kind === 'proposal') proposals.push(p.proposal);
    else if (p.kind === 'links') links.push(...p.links);
  });
  return (
    <div className={s.assistant} aria-live={working ? 'polite' : undefined} aria-busy={working || undefined}>
      {tools.length > 0 && (
        <div className={s.tools}>
          {tools.map((tool) => (
            <ToolChipView key={tool.id} tool={tool} />
          ))}
        </div>
      )}
      {text && <RichText text={text} />}
      {working && <Thinking />}
      {status === 'error' && !text.endsWith('This answer was interrupted.') && <Callout tone="danger">{t('This answer was interrupted.')}</Callout>}
      <Links links={links} />
      {proposals.map((p) => (
        <ProposalCard key={p.id} proposal={p} />
      ))}
    </div>
  );
}

/** The turn being streamed right now. */
export function LiveMessage({ turn }: { turn: LiveTurn }) {
  const waiting = turn.status === 'streaming' && !turn.text && !turn.tools.length;
  return (
    <div className={s.assistant} aria-live="polite" aria-busy={turn.status === 'streaming'}>
      {turn.tools.length > 0 && (
        <div className={s.tools}>
          {turn.tools.map((tool) => (
            <ToolChipView key={tool.id} tool={tool} />
          ))}
        </div>
      )}
      {waiting && <Thinking />}
      {turn.text && <RichText text={turn.text} />}
      {turn.status === 'streaming' && turn.text && <span className={s.caret} aria-hidden />}
      {turn.notes.map((n, i) => (
        <p key={i} className={s.note}>
          {n}
        </p>
      ))}
      <Links links={turn.links} />
      {turn.proposals.map((p) => (
        <ProposalCard key={p.id} proposal={p} />
      ))}
      {turn.status === 'error' && <Callout tone="danger">{turn.error ?? t('Something went wrong')}</Callout>}
    </div>
  );
}
